import type { IBudgetTransfer } from "@/modules/transaction/common/types";
import { regionCodeOptions } from "@/modules/transaction/helpers";
import { Currencies } from "@/shared/common/enums";
import { Amount } from "@/shared/components";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { type RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: IBudgetTransfer;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const BudgetTransferDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  const regionCode = useMemo(() => {
    const selectedRegionCode = regionCodeOptions.find(
      (item) => item.value === transfer.regionCode
    );
    return selectedRegionCode?.label || "";
  }, [transfer]);

  const messageLines = useMemo(() => {
    return [
      {
        key: "transfer.budgetTransfer.messageLineOne",
        value: transfer.messages?.message1,
      },
      {
        key: "transfer.budgetTransfer.messageLineTwo",
        value: transfer.messages?.message2
      },
      {
        key: "transfer.budgetTransfer.messageLineThree",
        value: transfer.messages?.message3
      },
      {
        key: "transfer.budgetTransfer.messageLineFour",
        value: transfer.messages?.message4
      },
      {
        key: "transfer.budgetTransfer.messageLineFive",
        value: transfer.messages?.message5
      },
      {
        key: "transfer.budgetTransfer.messageLineSix",
        value: transfer.messages?.message6
      }
    ];
  }, [transfer]);

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.amount")}
                      </Text>
                      <Amount
                        currency={transfer.currency}
                        amount={transfer.amount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.receiver")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-beneficiary-tooltip"
                        text={transfer.beneficiary as string}
                      />
                      <Text
                        size="medium"
                        weight="bold"
                        className="truncate cursor-pointer pl-16"
                        id="account-transfer-beneficiary-tooltip"
                      >
                        {transfer.beneficiary}
                      </Text>
                    </div>
                    <Divider
                      className="mt-16"
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div
                      className="flexbox justify-content--between align-items--center pt-18 pb-18 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.accountFrom")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.debitAccount?.number as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.debitAccount?.number}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div
                      className="flexbox justify-content--between align-items--center pt-18 pb-18 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.receiverAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.creditBudgetAccount as string}
                        />
                        <Text size="medium" weight="bold">{transfer.creditBudgetAccount}</Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.budgetTransfer.payer")}
                      </Text>
                      <Text
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.isAuthorizedTransfer ?
                          t("transfer.budgetTransfer.thirdPart") :
                          t("transfer.budgetTransfer.myself")
                        }
                      </Text>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.regionalCode")}
                      </Text>
                      <Text
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {regionCode}
                      </Text>
                    </div>
                  </div>
                  {!!transfer.urgentTransferCommissionAmount && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("general.urgentCommissionFee")}
                        </Text>
                        <Amount
                          currency={Currencies.AMD}
                          amount={transfer.urgentTransferCommissionAmount}
                        />
                      </div>
                    </div>
                  )}
                  {transfer.commissionAccount?.number && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div
                        className="flexbox justify-content--between align-items--center pt-18 pb-18 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.feeFrom")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Copy
                            textAfterCopy={t("general.copied")}
                            size="medium"
                            parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                            text={transfer.commissionAccount?.number}
                          />
                          <Text size="medium" weight="bold">
                            {transfer.commissionAccount?.number}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="account-transfer-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        id="account-transfer-purpose-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                  </div>
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.budgetTransfer.name")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Text size="medium" weight="bold">
                          {transfer.authorizedBudgetTransferParty?.fullName}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.budgetTransfer.residence")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Text size="medium" weight="bold">
                          {transfer.authorizedBudgetTransferParty?.residency}
                        </Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                  </div>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.budgetTransfer.legalStatus")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Text size="medium" weight="bold">
                          {transfer.authorizedBudgetTransferParty?.partyType}
                        </Text>
                      </div>
                    </div>
                  </div>
                  {transfer.authorizedBudgetTransferParty?.tin && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.tinCode")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.tin}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {transfer.authorizedBudgetTransferParty?.documentType && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.passportType")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.documentType}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {transfer.authorizedBudgetTransferParty?.documentNumber && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.passportData")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.documentNumber}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {transfer.authorizedBudgetTransferParty?.address && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.address")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.address}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {transfer.authorizedBudgetTransferParty?.authorizedPerson && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.authorizedPerson")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.authorizedPerson}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {transfer.authorizedBudgetTransferParty?.additionalInfo && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.otherInformation")}
                        </Text>
                        <div className="flexbox align-items--center">
                          <Text size="medium" weight="bold">
                            {transfer.authorizedBudgetTransferParty?.additionalInfo}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ),
              value: "authorized_budget_transfer_party",
              isOpen: true,
              isHidden: !transfer.isAuthorizedTransfer,
              title: t("transfer.budgetTransfer.makeTransferAnotherPerson")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text type="secondary" size="medium" weight="semibold">
                        {t("transfer.tin")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.tin}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text type="secondary" size="medium" weight="semibold">
                        {t("general.date")}
                      </Text>
                      <Text size="medium" weight="bold">
                        {transfer.scheduleDate}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Divider
                      color="light"
                      type="primary"
                      isHorizontal
                    />
                    <div
                      className="flexbox justify-content--between align-items--center pt-18 pb-18 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                  </div>
                  {!!transfer.messages.reportCode && (
                    <div>
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                      <div
                        className="flexbox justify-content--between align-items--center pt-18 pb-18 detail-row">
                        <Text
                          className="text-nowrap"
                          type="secondary"
                          size="medium"
                          weight="semibold"
                        >
                          {t("transfer.budgetTransfer.reportCode")}
                        </Text>
                        <div className="flexbox align-items--center">
                          {transfer.messages?.reportCode && (
                            <Copy
                              textAfterCopy={t("general.copied")}
                              size="medium"
                              parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                              text={transfer.messages?.reportCode as string}
                            />
                          )}
                          <Text size="medium" weight="bold">
                            {transfer.messages?.reportCode}
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                  {messageLines.map((line, i) => {
                    if (line.value) {
                      return (
                        <div key={line.key}>
                          {!!i && (
                            <Divider
                              color="light"
                              type="primary"
                              isHorizontal
                            />
                          )}
                          <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                            <Text
                              className="text-nowrap"
                              type="secondary"
                              size="medium"
                              weight="semibold"
                            >
                              {t(line.key)}
                            </Text>
                            <div className="flexbox align-items--center">
                              <Text size="medium" weight="bold">
                                {line.value}
                              </Text>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
